/**
 * =============================================================================
 * IDS CRM Web - Forms filled in on a work log: the list and the viewer
 * =============================================================================
 *
 * Gareth, 12 October 2026: a form filled in on a work log should be viewable
 * from the customer Timeline, and from the ticket and job Work Logs tabs,
 * not only from the robot's own page.
 *
 *  - WorkLogFormList: one button per form (its name, the robot, done or in
 *    progress, how many photos). The lists come from the server already
 *    chosen: the Timeline's leave van checks out, the Work Logs tabs' do not.
 *  - WorkLogFormViewer: a window showing one form read only. The form is
 *    read when the window opens (GET /api/v1/work-logs/forms/:checkId), so a
 *    Timeline of fifty jobs does not carry every answer and photo with it.
 *
 * Nothing here changes a form: there is no save.
 * =============================================================================
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../utils/format';
import CheckFormReview from './CheckFormReview';
import './WorkLogFormViewer.css';

export const FORM_NOT_READ_MESSAGE = 'The form could not be opened. Check your signal and try again.';

const WORK_TYPE_LABELS = {
  site_work: 'Site Work',
  remote_session: 'Remote Session',
  remote_support: 'Remote Support',
  diagnosis: 'Diagnosis',
  phone_call: 'Phone Call',
  whatsapp: 'WhatsApp',
  handover_note: 'Handover Note',
  parts_fit: 'Parts Fit',
  customer_update: 'Customer Update'
};

const photosText = (count) => {
  const total = Number(count) || 0;
  if (total === 0) return '';
  return total === 1 ? '1 photo' : `${total} photos`;
};

/** 'Brush Robot Check · BR-001 · Completed · 3 photos' */
export function formLine(form) {
  return [
    form.name || 'Check form',
    form.asset_ref || null,
    form.check_completed ? 'Completed' : 'In progress',
    photosText(form.photo_count) || null
  ].filter(Boolean).join(' · ');
}

/**
 * The forms filled in on a ticket, a job or one work log, each opening the
 * viewer. Shows nothing when there are none.
 *
 * @param {object[]} props.forms summaries from the server
 * @param {(form: object) => void} props.onOpen
 * @param {string} [props.label]
 */
export function WorkLogFormList({ forms, onOpen, label = 'Forms' }) {
  const rows = Array.isArray(forms) ? forms.filter(form => form && form.check_id) : [];
  if (rows.length === 0) return null;
  return (
    <div className="work-log-forms" role="group" aria-label="Forms filled in">
      <span className="work-log-forms-label">{label}</span>
      {rows.map(form => (
        <button
          key={form.check_id}
          type="button"
          className={`work-log-form-chip ${form.check_completed ? 'completed' : 'in-progress'}`}
          title="Open this form"
          onClick={(event) => {
            // A Timeline row can itself be a button: opening a form is not a click on the row.
            event.stopPropagation();
            onOpen(form);
          }}
        >
          {formLine(form)}
        </button>
      ))}
    </div>
  );
}

/**
 * One filled-in form, read only.
 *
 * @param {string} props.checkId
 * @param {Function} props.authFetch
 * @param {Function} props.onClose
 * @param {boolean} [props.linkToWork] link the ticket or job the form was filled in on (not on that page itself)
 */
function WorkLogFormViewer({ checkId, authFetch, onClose, linkToWork = true }) {
  const [state, setState] = useState({ status: 'loading', form: null, message: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', form: null, message: null });
    (async () => {
      try {
        const response = await authFetch(`/api/v1/work-logs/forms/${encodeURIComponent(checkId)}`);
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setState({ status: 'failed', form: null, message: data.message || FORM_NOT_READ_MESSAGE });
          return;
        }
        setState({ status: 'ready', form: data, message: null });
      } catch (err) {
        if (!cancelled) setState({ status: 'failed', form: null, message: FORM_NOT_READ_MESSAGE });
      }
    })();
    return () => { cancelled = true; };
  }, [checkId, authFetch]);

  const { status, form, message } = state;
  const title = form?.name || 'Form';
  const workRef = form?.ticket_ref || form?.task_ref || null;
  const workPath = form?.ticket_id ? `/tickets/${form.ticket_id}` : (form?.task_id ? `/tasks/${form.task_id}` : null);
  const workType = form ? (WORK_TYPE_LABELS[form.work_log_type] || form.work_log_type || null) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal generic-check-detail-modal work-log-form-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {status === 'loading' && <p className="work-log-form-status" role="status">Opening the form...</p>}
          {status === 'failed' && <div className="work-log-form-error" role="alert">{message}</div>}
          {status === 'ready' && form && (
            <>
              <div className="robot-check-summary">
                <div className="summary-item">
                  <label>Filled in</label>
                  <span>{formatDateTime(form.submitted_at)}</span>
                </div>
                <div className="summary-item">
                  <label>By</label>
                  <span>{form.submitted_by_name || 'Unknown'}</span>
                </div>
                {workRef && (
                  <div className="summary-item">
                    <label>{form.ticket_id ? 'Ticket' : 'Job'}</label>
                    <span className="record-ref">
                      {linkToWork && workPath ? <Link to={workPath}>{workRef}</Link> : workRef}
                    </span>
                  </div>
                )}
                {form.customer_name && (
                  <div className="summary-item">
                    <label>Customer</label>
                    <span>{form.customer_name}</span>
                  </div>
                )}
                {form.asset_ref && (
                  <div className="summary-item">
                    <label>Robot</label>
                    <span>{form.asset_ref}</span>
                  </div>
                )}
                {workType && (
                  <div className="summary-item">
                    <label>Work type</label>
                    <span>{workType}</span>
                  </div>
                )}
                <div className="summary-item">
                  <label>Status</label>
                  <span className={`status-chip ${form.check_completed ? 'completed' : 'in_progress'}`}>
                    {form.check_completed ? 'Completed' : 'In Progress'}
                  </span>
                </div>
              </div>

              <CheckFormReview check={form} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkLogFormViewer;
